// Row contracts of the screens tables (SCREENS-S1; tables/cell/screens.ts). Same conventions as
// `rows.ts`: keys are column names, nullability is the column's, enums are the CHECK's; bound in
// `contract-bindings.ts`. The API views (api/screens.ts) are narrower: a lease's `target` (where it
// runs on the stack) never leaves the server.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  BrowserAccountGrantLevel,
  BrowserAccountGrantSubject,
  BrowserAccountKind,
  BrowserAccountStatus,
  BrowserLiveMode,
  ScreenAskState,
  ScreenLeaseEndReason,
  ScreenLeaseHolder,
  ScreenLeaseKind,
  ScreenLeaseOrigin,
  ScreenLeasePurpose,
  ScreenLeaseState,
  ScreenNeedsYouWhy,
  ScreenStackPlacement,
  ScreenStackState,
} from "./enums";
import { Timestamp, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

export const ScreenStackHealth = z
  .object({ browsers: z.string(), desktops: z.string(), live: z.string() })
  .meta({ id: "ScreenStackHealth" });
export const ScreenStackCapacity = z
  .object({
    browsers_max: z.int().min(0),
    browsers_used: z.int().min(0),
    desktops_max: z.int().min(0),
    desktops_used: z.int().min(0),
  })
  .meta({ id: "ScreenStackCapacity" });

export const ScreenStackRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    name: z.string(),
    placement: ScreenStackPlacement,
    browsers_api: z.string(),
    desktops_api: z.string(),
    live_url: z.string(),
    browser_live: BrowserLiveMode,
    credential_ref_id: z.string(),
    credential_version: z.int().min(1),
    versions: z.record(z.string(), z.string()),
    health: ScreenStackHealth,
    capacity: ScreenStackCapacity,
    state: ScreenStackState,
    last_seen_at: Timestamp,
    ...stamps,
  })
  .meta({ id: "ScreenStackRow" });

export const BrowserAccountRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    handle: z.string(),
    // PERSISTENT-SCREENS: a desktop profile has no origin.
    kind: BrowserAccountKind,
    origin: z.string().nullable(),
    label: z.string(),
    use_for: z.string().nullable(),
    owner_membership_id: Uuid,
    status: BrowserAccountStatus,
    last_used_at: Timestamp.nullable(),
    revoked_at: Timestamp.nullable(),
    wiped_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "BrowserAccountRow" });

export const BrowserAccountGrantRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    account_id: Uuid,
    subject_kind: BrowserAccountGrantSubject,
    subject_id: Uuid,
    level: BrowserAccountGrantLevel,
    granted_by_membership_id: Uuid,
    revoked_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "BrowserAccountGrantRow" });

export const ScreenLeaseRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    kind: ScreenLeaseKind,
    account_id: Uuid.nullable(),
    stack_id: Uuid.nullable(),
    holder_membership_id: Uuid,
    holder_kind: ScreenLeaseHolder,
    run_id: Uuid.nullable(),
    task_id: Uuid.nullable(),
    origin: ScreenLeaseOrigin,
    priority: z.int(),
    reason: z.string().nullable(),
    state: ScreenLeaseState,
    queued_at: Timestamp,
    started_at: Timestamp.nullable(),
    last_activity_at: Timestamp.nullable(),
    idle_timeout_s: z.int(),
    controller_membership_id: Uuid.nullable(),
    control_gen: z.int().min(0),
    needs_you_why: ScreenNeedsYouWhy.nullable(),
    needs_you_note: z.string().nullable(),
    hand_back_note: z.string().nullable(),
    title: z.string().nullable(),
    ended_at: Timestamp.nullable(),
    end_reason: ScreenLeaseEndReason.nullable(),
    start_attempts: z.int(),
    next_start_at: Timestamp.nullable(),
    purpose: ScreenLeasePurpose,
    resume_screenshot: z.boolean(),
    agent_notice: z.string().nullable(),
    login_checked_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "ScreenLeaseRow" });

export const ScreenAskRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    lease_id: Uuid,
    account_id: Uuid.nullable(),
    agent_membership_id: Uuid,
    owner_membership_id: Uuid.nullable(),
    why: ScreenNeedsYouWhy,
    reason: z.string().nullable(),
    state: ScreenAskState,
    thread_id: Uuid.nullable(),
    message_id: Uuid.nullable(),
    expires_at: Timestamp,
    resolved_by_membership_id: Uuid.nullable(),
    resolved_at: Timestamp.nullable(),
    extensions: z.int().min(0).max(16),
    ...actor,
    ...stamps,
  })
  .meta({ id: "ScreenAskRow" });
